import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/apiClient";
import { isSupplierApproved, supplierOptions } from "../../lib/suppliers";
import { UnapprovedSupplierDialog } from "./UnapprovedSupplierDialog";
import { ApprovedSuppliersPanel } from "./ApprovedSuppliersPanel";

const NAME_FIELD = { line_items: "supplier_name", access_hire: "supplier_name" };
const TYPE = { line_items: "materials", access_hire: "access_hire" };

// Approved-supplier checks for Parts & Materials and Access Hire rows.
export function useSupplierCheck({ category, setQuote, user }) {
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const catRef = useRef(category);
  catRef.current = category;
  const supRef = useRef(suppliers);
  supRef.current = suppliers;
  const enabledRef = useRef(false);
  enabledRef.current = !!settings?.enabled;

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
    api.get("/supplier-settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const patchLine = useCallback((section, lineId, patch) => {
    setQuote((q) => ({ ...q, [section]: (q[section] || []).map((l) => (l.id === lineId ? { ...l, ...patch } : l)) }));
  }, [setQuote]);

  const check = useCallback(({ section, lineId, name, source, rec }) => {
    if (!enabledRef.current) return;
    const n = (name || "").trim();
    if (!n) {
      if (rec) patchLine(section, lineId, { unapproved_supplier: null });
      return;
    }
    if (isSupplierApproved(supRef.current, n, TYPE[section], catRef.current)) {
      if (rec) patchLine(section, lineId, { unapproved_supplier: null });
      return;
    }
    if (rec && rec.name === n) return;
    setQueue((qq) => [...qq.filter((p) => p.lineId !== lineId), { section, lineId, name: n, source }]);
  }, [patchLine]);

  const pending = queue[0] || null;
  const shift = () => setQueue((qq) => qq.slice(1));

  const onConfirm = () => {
    patchLine(pending.section, pending.lineId, {
      unapproved_supplier: {
        name: pending.name, category: catRef.current || "",
        confirmed_by: user?.name || user?.email || "", confirmed_at: new Date().toISOString(),
      },
    });
    shift();
  };

  const onChange = () => {
    const { section, lineId, source } = pending;
    if (source === "drag") {
      setQuote((q) => ({ ...q, [section]: (q[section] || []).filter((l) => l.id !== lineId) }));
    } else {
      patchLine(section, lineId, { [NAME_FIELD[section]]: "", unapproved_supplier: null });
    }
    shift();
  };

  const elements = createPortal(
    <>
      <UnapprovedSupplierDialog pending={pending} settings={settings} category={category} onConfirm={onConfirm} onChange={onChange} />
      {settings?.enabled && ["materials", "access_hire"].map((t) => (
        <datalist key={t} id={`supplier-options-${t}`} data-testid={`supplier-options-${t}`}>
          {supplierOptions(suppliers, t, category).map((o) => <option key={o.name} value={o.name} label={o.label} />)}
        </datalist>
      ))}
      <ApprovedSuppliersPanel open={panelOpen} onClose={() => setPanelOpen(false)} suppliers={suppliers} category={category} />
    </>,
    document.body,
  );

  return { check, elements, enabled: !!settings?.enabled, openPanel: () => setPanelOpen(true) };
}
